import { useEffect, useMemo, useState } from 'react';
import { reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { AllocationTargetPicker } from './AllocationTargetPicker';

/**
 * Manual match modal (plan §8).
 *
 * Attach a single statement line to a single internal record (invoice
 * payment, purchase payment, bank transaction, nominal category). For
 * multi-allocation matches the caller should use SplitAllocationModal
 * instead.
 *
 * Flow:
 *   1. User picks a match_type (suggested default based on sign of amount).
 *   2. User picks an allocation target (picker component).
 *   3. Submit creates a draft match with one allocation. A "Reconcile now"
 *      checkbox lets the user atomically transition it to reconciled.
 */
const MATCH_TYPES_IN = [
  { value: 'invoice_payment', label: 'Customer invoice payment' },
  { value: 'refund_supplier', label: 'Supplier refund (money in)' },
  { value: 'bank_interest', label: 'Bank interest' },
  { value: 'proprietor_contribution_in', label: 'Proprietor contribution' },
  { value: 'internal_transfer', label: 'Transfer in' },
  { value: 'loan_drawdown', label: 'Loan drawdown' },
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'opening_balance', label: 'Opening balance' },
  { value: 'other', label: 'Other (money in)' },
];

const MATCH_TYPES_OUT = [
  { value: 'purchase_payment', label: 'Supplier / purchase payment' },
  { value: 'refund_customer', label: 'Customer refund (money out)' },
  { value: 'bank_charge', label: 'Bank charge / fee' },
  { value: 'proprietor_drawing_out', label: 'Proprietor drawing' },
  { value: 'personal_reimbursement', label: 'Reimburse proprietor' },
  { value: 'internal_transfer', label: 'Transfer out' },
  { value: 'credit_card_settlement', label: 'Credit card settlement' },
  { value: 'loan_repayment', label: 'Loan repayment' },
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'other', label: 'Other (money out)' },
];

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

export function ManualMatchModal({ open, onOpenChange, line, bankAccountId, onMatched }) {
  const lineAmount = Number(line?.amount ?? 0);
  const isInflow = lineAmount > 0;
  const matchTypeOptions = isInflow ? MATCH_TYPES_IN : MATCH_TYPES_OUT;

  const [matchType, setMatchType] = useState(
    isInflow ? 'invoice_payment' : 'purchase_payment'
  );
  const [matchDate, setMatchDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [allocatedAmount, setAllocatedAmount] = useState('');
  const [target, setTarget] = useState({
    target_type: 'bank_transaction',
    target_id: null,
    nominal_account_id: null,
    description: '',
  });
  const [reconcileNow, setReconcileNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Reset form each time the modal opens for a new line.
    setError('');
    setDone(null);
    setBusy(false);
    setReconcileNow(false);
    setMatchType(lineAmount >= 0 ? 'invoice_payment' : 'purchase_payment');
    setMatchDate(line?.transaction_date ? String(line.transaction_date).slice(0, 10) : '');
    setReference(line?.reference || '');
    setNotes('');
    setAllocatedAmount(Math.abs(lineAmount).toFixed(2));
    setTarget({
      target_type: 'bank_transaction',
      target_id: null,
      nominal_account_id: null,
      description: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id]);

  const canSubmit = useMemo(() => {
    if (!line || !bankAccountId) return false;
    if (!matchType) return false;
    const amt = Number(allocatedAmount);
    if (!Number.isFinite(amt) || amt <= 0) return false;
    if (target.target_type === 'nominal_category') return !!target.nominal_account_id;
    return !!target.target_id;
  }, [line, bankAccountId, matchType, allocatedAmount, target]);

  const submit = async () => {
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      const totalAmount = Number(allocatedAmount);
      const created = await reconciliationAPI.createMatch({
        bank_account_id: bankAccountId,
        match_type: matchType,
        match_strategy: 'manual',
        total_amount: totalAmount,
        match_date: matchDate || undefined,
        reference: reference || null,
        notes: notes || null,
        allocations: [
          {
            statement_line_id: line.id,
            allocated_amount: totalAmount,
            target_type: target.target_type,
            target_id: target.target_id || null,
            nominal_account_id: target.nominal_account_id || null,
            description: target.description || null,
          },
        ],
      });

      if (reconcileNow) {
        await reconciliationAPI.reconcileMatch(created.match.id);
      }

      setDone({ match: created.match, reconciled: reconcileNow });
      if (onMatched) onMatched(created.match);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not create match');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Match statement line">
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Line summary */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>
                    {line?.transaction_date
                      ? new Date(line.transaction_date).toISOString().slice(0, 10)
                      : '—'}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate" title={line?.description || ''}>
                    {line?.description || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Reference</div>
                  <div>{line?.reference || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Counterparty</div>
                  <div className="truncate">{line?.counterparty || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div className={lineAmount >= 0 ? 'text-green-700 dark:text-green-400 font-semibold' : 'text-red-700 dark:text-red-400 font-semibold'}>
                    {formatMoney(lineAmount)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs text-green-800 dark:text-green-200">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Match created{done.reconciled ? ' and reconciled' : ' (draft)'}.
                </div>
                <div className="mt-2">
                  <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Match type</FormLabel>
                  <Select value={matchType} onChange={(e) => setMatchType(e.target.value)}>
                    {matchTypeOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Match date</FormLabel>
                  <Input
                    type="date"
                    value={matchDate}
                    onChange={(e) => setMatchDate(e.target.value)}
                  />
                </div>
                <div>
                  <FormLabel>Allocated amount</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={allocatedAmount}
                    onChange={(e) => setAllocatedAmount(e.target.value)}
                  />
                </div>
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. INV-1234"
                  />
                </div>
              </div>

              <div>
                <FormLabel>Allocation target</FormLabel>
                <AllocationTargetPicker
                  bankAccountId={bankAccountId}
                  lineAmount={lineAmount}
                  value={target}
                  onChange={setTarget}
                />
              </div>

              <div>
                <FormLabel>Notes</FormLabel>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal memo"
                />
              </div>

              <div className="flex items-center gap-2 text-xs">
                <input
                  id="reconcile-now"
                  type="checkbox"
                  checked={reconcileNow}
                  onChange={(e) => setReconcileNow(e.target.checked)}
                />
                <label htmlFor="reconcile-now">
                  Reconcile immediately (otherwise save as draft)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit || busy}>
                  {busy ? 'Saving…' : reconcileNow ? 'Create & reconcile' : 'Create draft match'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

export default ManualMatchModal;
